import {
	Caption2,
	Combobox,
	Label,
	makeStyles,
	Option,
	useId
} from '@fluentui/react-components';
import { useEffect, useState } from 'react';
import { useFetchGamesQuery } from '../../Api/Games/gamesSlice';
import {
	NewReviewFormReducerAction,
	NewReviewFormReducerActionTypes,
	NewReviewFormState
} from '../Types';
import BoxArt from '../../Shared/BoxArt';

export interface GameSelectorProps {
	formContext: NewReviewFormState;
	formDispatch: React.Dispatch<NewReviewFormReducerAction>;
	gameTitle?: string;
	platformName?: string;
}

export default function GameSelector(props: GameSelectorProps) {
	const styles = useStyles();
	const id = useId('review');

	const [comboValue, setComboValue] = useState<string>('');
	const { data: games } = useFetchGamesQuery({
		gameTitleStartsWith: comboValue
	});

	useEffect(() => {
		// Set default values when supplied in query string
		const queryParamGame = games?.find((x) => x.title === props.gameTitle);
		if (queryParamGame) {
			props.formDispatch({
				type: NewReviewFormReducerActionTypes.setGame,
				payload: queryParamGame
			});

			// If there is only one platform it was already automagically set, otherwise see if we were given a valid platform to set
			if (queryParamGame.availableOnConsoles.length > 1) {
				const platform = queryParamGame.availableOnConsoles.find(
					(p) => p.name === props.platformName
				);
				if (platform) {
					props.formDispatch({
						type: NewReviewFormReducerActionTypes.setPlatform,
						payload: platform
					});
				}
			}
		}
	}, [games, props]);

	return (
		<div className={styles.form}>
			<div className={styles.gameDropdown}>
				<div className={styles.inputWithLabel}>
					<Label
						htmlFor={`${id}-game-combobox`}
						style={{ textAlign: 'left' }}
					>
						Select a Game
					</Label>
					<Combobox
						id={`${id}-game-combobox`}
						placeholder='Select a Game to begin'
						selectedOptions={['']}
						value={comboValue}
						onOptionSelect={(_event, data) => {
							//TODO: test that removing the 'onChange' didn't break anything here
							setComboValue(data.optionValue ?? '');
							const selectedGame = games?.find(
								(x) => x.title === data.optionValue
							);
							if (selectedGame) {
								props.formDispatch({
									type: NewReviewFormReducerActionTypes.setGame,
									payload: selectedGame
								});
							}
						}}
					>
						{games?.map((game, index) => (
							<Option
								key={index}
								text={game.title}
								value={game.title}
							>
								<div className={styles.gameItem}>
									{game.boxArtUrl && (
										<BoxArt
											imageSrc={game.boxArtUrl}
											altText=''
											containerOptions={{
												height: '25px',
												width: '25px'
											}}
											imageOptions={{
												minHeight: '100%'
											}}
										/>
									)}
									<Caption2>{game.title}</Caption2>
								</div>
							</Option>
						))}
						{!games || games.length === 0 ? (
							<Option
								key='no-results'
								text=''
							>
								No Results Found
							</Option>
						) : null}
					</Combobox>
				</div>
			</div>
		</div>
	);
}

const useStyles = makeStyles({
	form: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'start',
		rowGap: '25px'
	},
	inputWithLabel: {
		display: 'flex',
		flexDirection: 'column',
		gap: '2px',
		maxWidth: '400px'
	},
	gameItem: {
		display: 'flex',
		columnGap: '5px',
		alignItems: 'center'
	},
	gameDropdown: {
		display: 'flex',
		alignItems: 'center',
		columnGap: '6px'
	}
});
